const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const data: ApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Request failed');
  }

  return data.data as T;
}

export const api = {
  // Auth
  requestOtp: (email: string) =>
    request('/auth/request-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyOtp: (email: string, code: string) =>
    request<{ token: string; user: any; expiresAt: number }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  logout: () =>
    request('/auth/logout', { method: 'POST' }),

  getMe: () =>
    request<{ email: string; name?: string; settings?: any }>('/auth/me'),

  // Documents
  getUploadUrl: (data: {
    fileName: string;
    fileType: string;
    documentType: string;
    year: number;
    employer?: string;
  }) =>
    request<{ documentId: string; uploadUrl: string; s3Key: string }>(
      '/documents/upload-url',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  listDocuments: (params?: { year?: number; type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.year) searchParams.set('year', params.year.toString());
    if (params?.type) searchParams.set('type', params.type);
    return request<{ documents: any[]; total: number }>(
      `/documents?${searchParams.toString()}`
    );
  },

  getDocument: (id: string) =>
    request<any>(`/documents/${id}`),

  processDocument: (id: string, year: number) =>
    request<any>(`/documents/${id}/process?year=${year}`, { method: 'POST' }),

  deleteDocument: (id: string) =>
    request(`/documents/${id}`, { method: 'DELETE' }),

  updateDocument: (id: string, data: { extractedData: Record<string, any> }) =>
    request<{ message: string; extractedData: any }>(`/documents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Businesses
  listBusinesses: (params?: { year?: number }) =>
    request<{ businesses: any[]; total: number }>(
      params?.year ? `/businesses?year=${params.year}` : '/businesses'
    ),

  createBusiness: (data: any) =>
    request('/businesses', { method: 'POST', body: JSON.stringify(data) }),

  getBusiness: (id: string, year?: number) => {
    const params = year ? `?year=${year}` : '';
    return request<any>(`/businesses/${id}${params}`);
  },

  updateBusiness: (id: string, data: any) =>
    request(`/businesses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteBusiness: (id: string) =>
    request(`/businesses/${id}`, { method: 'DELETE' }),

  // Expenses
  listExpenses: (params?: { businessId?: string; year?: number; category?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.businessId) searchParams.set('businessId', params.businessId);
    if (params?.year) searchParams.set('year', params.year.toString());
    if (params?.category) searchParams.set('category', params.category);
    return request<{ expenses: any[]; total: number; totalAmount: number; byCategory: any }>(
      `/expenses?${searchParams.toString()}`
    );
  },

  addExpense: (data: any) =>
    request('/expenses', { method: 'POST', body: JSON.stringify(data) }),

  updateExpense: (id: string, data: any) =>
    request(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteExpense: (id: string) =>
    request(`/expenses/${id}`, { method: 'DELETE' }),

  importExpenses: (data: { businessId: string; year: number; csvData: string }) =>
    request<{
      message: string;
      imported: number;
      totalAmount: number;
      byCategory: Record<string, number>;
      records: Array<{
        id: string;
        date: string;
        category: string;
        description: string;
        amount: number;
        vendor?: string;
      }>;
    }>('/expenses/import', { method: 'POST', body: JSON.stringify(data) }),

  // Income
  listIncome: (params?: { businessId?: string; year?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.businessId) searchParams.set('businessId', params.businessId);
    if (params?.year) searchParams.set('year', params.year.toString());
    return request<{ income: any[]; total: number; totalAmount: number }>(
      `/income?${searchParams.toString()}`
    );
  },

  addIncome: (data: any) =>
    request('/income', { method: 'POST', body: JSON.stringify(data) }),

  updateIncome: (id: string, data: any) =>
    request(`/income/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  importIncome: (data: { businessId: string; year: number; csvData: string }) =>
    request<{
      message: string;
      imported: number;
      totalAmount: number;
      records: Array<{
        id: string;
        date: string;
        client: string;
        description: string;
        amount: number;
      }>;
    }>('/income/import', { method: 'POST', body: JSON.stringify(data) }),

  deleteIncome: (id: string) =>
    request(`/income/${id}`, { method: 'DELETE' }),

  // Investments
  listInvestments: (params?: { year?: number; type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.year) searchParams.set('year', params.year.toString());
    if (params?.type) searchParams.set('type', params.type);
    return request<any>(`/investments?${searchParams.toString()}`);
  },

  importCrypto: (data: { exchange: string; year: number; csvData: string }) =>
    request('/investments/import-crypto', { method: 'POST', body: JSON.stringify(data) }),

  importStocks: (data: { broker: string; year: number; csvData: string }) =>
    request('/investments/import-stocks', { method: 'POST', body: JSON.stringify(data) }),

  calculateGains: (year: number, method: string = 'fifo') =>
    request('/investments/calculate', {
      method: 'POST',
      body: JSON.stringify({ year, method }),
    }),

  // Taxes
  getTaxSummary: (year: number) =>
    request<any>(`/taxes/${year}/summary`),

  getDeductions: (year: number) =>
    request<any>(`/taxes/${year}/deductions`),

  addDeduction: (year: number, data: any) =>
    request(`/taxes/${year}/deductions`, { method: 'POST', body: JSON.stringify(data) }),

  deleteDeduction: (year: number, id: string) =>
    request(`/taxes/${year}/deductions/${id}`, { method: 'DELETE' }),

  addClaimOfRight: (year: number, data: any) =>
    request(`/taxes/${year}/deductions`, { method: 'POST', body: JSON.stringify(data) }),

  updateTaxYear: (year: number, data: any) =>
    request(`/taxes/${year}`, { method: 'PUT', body: JSON.stringify(data) }),

  exportTaxData: (year: number, format: string) =>
    request(`/taxes/${year}/export`, { method: 'POST', body: JSON.stringify({ format }) }),

  exportExcel: async (year: number): Promise<void> => {
    const result = await request<{
      fileName: string;
      fileData: string;
      year: number;
    }>(`/taxes/${year}/export-excel`);

    // Decode base64 to binary and trigger download
    const raw = atob(result.fileData);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Analytics
  getYearOverYear: (startYear?: number, endYear?: number) => {
    const params = new URLSearchParams();
    if (startYear) params.set('startYear', startYear.toString());
    if (endYear) params.set('endYear', endYear.toString());
    return request<any>(`/analytics/year-over-year?${params.toString()}`);
  },

  // AI Chat
  chat: (data: {
    message: string;
    provider?: 'openai' | 'anthropic';
    includeContext?: boolean;
    year?: number;
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  }) =>
    request<{
      response: string;
      provider: string;
      contextIncluded: boolean;
      actions?: Array<{
        tool: string;
        input: any;
        result: {
          success: boolean;
          message: string;
          data?: any;
        };
      }>;
    }>(
      '/ai/chat',
      { method: 'POST', body: JSON.stringify(data) }
    ),

  // AI Conversation History
  getAiHistory: (params?: { limit?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    return request<{
      conversations: Array<{
        id: string;
        userMessage: string;
        assistantResponse: string;
        provider: string;
        actions: Array<{
          tool: string;
          input: any;
          result: {
            success: boolean;
            message: string;
            data?: any;
          };
        }>;
        year: number;
        createdAt: string;
      }>;
      count: number;
      total: number;
    }>(`/ai/history?${searchParams.toString()}`);
  },

  // Settings
  getSettings: () =>
    request<{
      settings: {
        anthropicApiKey: string | null;
        openaiApiKey: string | null;
        preferredAiProvider: 'anthropic' | 'openai';
        aiContextEnabled: boolean;
        hasAnthropicKey: boolean;
        hasOpenaiKey: boolean;
      };
    }>('/settings'),

  updateSettings: (data: {
    anthropicApiKey?: string | null;
    openaiApiKey?: string | null;
    preferredAiProvider?: 'anthropic' | 'openai';
    aiContextEnabled?: boolean;
    clearAnthropicKey?: boolean;
    clearOpenaiKey?: boolean;
  }) =>
    request<{
      message: string;
      settings: {
        anthropicApiKey: string | null;
        openaiApiKey: string | null;
        preferredAiProvider: 'anthropic' | 'openai';
        aiContextEnabled: boolean;
        hasAnthropicKey: boolean;
        hasOpenaiKey: boolean;
      };
    }>('/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // User Data Management
  resetData: () =>
    request<{
      message: string;
      deleted: {
        documents: number;
        businesses: number;
        expenses: number;
        income: number;
        investments: number;
        deductions: number;
      };
    }>('/user/reset-data', { method: 'POST' }),

  migrateYear: (fromYear: number, toYear: number) =>
    request<{
      message: string;
      fromYear: number;
      toYear: number;
      migrated: {
        expenses: number;
        income: number;
        investments: number;
        deductions: number;
        documents: number;
      };
    }>('/user/migrate-year', { method: 'POST', body: JSON.stringify({ fromYear, toYear }) }),

  // Tasks
  listTasks: (params?: { showCompleted?: boolean }) => {
    const searchParams = new URLSearchParams();
    if (params?.showCompleted !== undefined) {
      searchParams.set('showCompleted', params.showCompleted.toString());
    }
    return request<{
      tasks: Array<{
        id: string;
        title: string;
        description?: string;
        dueDate?: string;
        completed: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      completedCount: number;
      pendingCount: number;
    }>(`/tasks?${searchParams.toString()}`);
  },

  createTask: (data: { title: string; description?: string; dueDate?: string }) =>
    request<{
      task: {
        id: string;
        title: string;
        description?: string;
        dueDate?: string;
        completed: boolean;
        createdAt: string;
        updatedAt: string;
      };
      message: string;
    }>('/tasks', { method: 'POST', body: JSON.stringify(data) }),

  updateTask: (id: string, data: { title?: string; description?: string; dueDate?: string | null; completed?: boolean }) =>
    request<{
      task: {
        id: string;
        title?: string;
        description?: string;
        dueDate?: string | null;
        completed?: boolean;
        updatedAt: string;
      };
      message: string;
    }>(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteTask: (id: string) =>
    request<{ message: string }>(`/tasks/${id}`, { method: 'DELETE' }),

  // Smart Import (AI-powered) - uses Lambda Function URL for longer timeout
  smartImport: async (data: {
    fileContent: string;
    fileName: string;
    fileType: 'csv' | 'pdf' | 'txt' | 'json' | 'xlsx';
    year?: number;
  }): Promise<{
    fileName: string;
    fileType: string;
    analysis: {
      detectedType: 'crypto' | 'stocks' | 'expenses' | 'income' | 'deductions' | 'w2' | 'unknown';
      confidence: number;
      description: string;
      extractedData: any[];
      formattedCsv: string;
      csvHeaders: string[];
      warnings: string[];
      importInstructions: string;
    };
    message: string;
  }> => {
    // Use Lambda Function URL if available (bypasses API Gateway 29s limit)
    const smartImportUrl = import.meta.env.VITE_SMART_IMPORT_URL;
    const token = localStorage.getItem('token');

    if (smartImportUrl) {
      const response = await fetch(smartImportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Smart import failed');
      }

      return result.data;
    }

    // Fallback to API Gateway
    return request('/import/smart', { method: 'POST', body: JSON.stringify(data) });
  },

  // Reimport from exported Excel file
  reimportExcel: (data: { fileData: string; year: number }) =>
    request<{
      message: string;
      year: number;
      imported: {
        businesses: number;
        income: number;
        expenses: number;
        deductions: number;
        investments: number;
        tasks: number;
      };
    }>('/import/reimport', { method: 'POST', body: JSON.stringify(data) }),
};
